import React, { useMemo, useReducer } from 'react';
import { makeStyles } from '@material-ui/core';
import { Stepper } from '@modernhealth/vital-components';

import { memberClient } from 'src/config/Apollo';

import type { InvoicingPageAction, InvoicingPageState } from '../components/NewInvoicing/types';
import { steps } from '../components/NewInvoicing/steps';

export const NewInvoicingPage: React.FC = () => {
  const styles = useStyles();

  const [state, dispatch] = useReducer(invoicingPageReducer, {
    currentStep: 0,
    data: {
      visit: null,
      cptCode: null,
      diagnosisCodes: [null],
      pammIsClinicallyIndicatedForTheClient: null,
      clientIsInterestedInPursuingPammServices: null,
    },
  });

  const currentStep = useMemo<JSX.Element>(() => {
    const Component = steps[state.currentStep]?.component;

    if (!Component) {
      throw new Error(`Steps array doesn't container ${state.currentStep} index`);
    }

    return (
      <Component
        data={state.data}
        setData={(setDataFn) => dispatch({ type: 'SET_DATA', payload: setDataFn(state.data) })}
        prevStep={() => dispatch({ type: 'PREV_STEP' })}
        nextStep={() => dispatch({ type: 'NEXT_STEP' })}
        memberClient={memberClient}
      />
    );
  }, [state.currentStep, state.data]);

  return (
    <div className={styles.container}>
      <Stepper
        step={state.currentStep + 1}
        shortNames={steps.map((step) => step.title)}
        label={`${state.currentStep + 1} of ${steps.length}`}
        formatNextStep={(step) => `Next: ${step}`}
      />
      {currentStep}
    </div>
  );
};

const invoicingPageReducer = (
  state: InvoicingPageState,
  action: InvoicingPageAction,
): InvoicingPageState => {
  if (action.type === 'PREV_STEP') {
    return { ...state, currentStep: Math.max(0, state.currentStep - 1) };
  }

  if (action.type === 'NEXT_STEP') {
    return { ...state, currentStep: Math.min(steps.length - 1, state.currentStep + 1) };
  }

  if (action.type === 'SET_DATA') {
    return { ...state, data: action.payload };
  }

  return state;
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
    margin: '64px auto',
    maxWidth: 500,
    width: '100%',

    '& > div:nth-child(2)': {
      display: 'flex',
      flexDirection: 'column',
      gap: 40,
    },
  },
}));
